import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'

// Layouts (keep eager — needed on every page)
import PublicLayout from './components/layout/PublicLayout'
import MidnightLayout from './components/layout/MidnightLayout'
import ProtectedRoute from './components/shared/ProtectedRoute'

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-midnight-bg">
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
)

// Public Pages (lazy)
const HomePage = lazy(() => import('./pages/HomePage'))
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const FreeQRPage = lazy(() => import('./pages/FreeQRPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const StatusPage = lazy(() => import('./pages/StatusPage'))
const GettingStartedPage = lazy(() => import('./pages/GettingStartedPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))

// Protected Pages (lazy)
const StudioPage = lazy(() => import('./pages/StudioPage'))
const ContentDetailPage = lazy(() => import('./pages/ContentDetailPage'))
const UploadPage = lazy(() => import('./pages/UploadPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))

export default function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/getting-started" element={<GettingStartedPage />} />
            <Route path="/free-qrcodes" element={<FreeQRPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<PrivacyPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/status" element={<StatusPage />} />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRoute><MidnightLayout /></ProtectedRoute>}>
            <Route path="/studio" element={<StudioPage />} />
            <Route path="/studio/content/:id" element={<ContentDetailPage />} />
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Redirects */}
          <Route path="/create" element={<Navigate to="/upload" replace />} />
          <Route path="/profile" element={<Navigate to="/studio" replace />} />
          <Route path="/auth/discover" element={<Navigate to="/discover" replace />} />
          <Route path="/videoqr-getting-started" element={<Navigate to="/getting-started" replace />} />
          <Route path="/how-to" element={<Navigate to="/getting-started" replace />} />

          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center bg-midnight-bg">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-white mb-4">404</h1>
                <p className="text-text-secondary mb-8">Page not found</p>
                <a href="/" className="text-accent hover:text-accent-light">Go home</a>
              </div>
            </div>
          } />
        </Routes>
      </Suspense>
    </Router>
  )
}
